import { Calendar, FileText, TriangleAlert, Trash2, CheckCircle, LoaderCircle } from 'lucide-react';

import { Permission } from '../../../../../../backend/src/auth/permissions/permission';
import { RunStatus } from '../../../../../../backend/src/types/types';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { Button } from '@/components/atoms/new-button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/atoms/Tooltip';
import { isProcessingStatus } from '@/constants/pipelineStatus';
import { useUserContext } from '@/Contexts/User';
import formatDate from '@/helpers/formatDate';
import { cn } from '@/helpers/shadCnUtils';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Case } from '@/types';

interface CaseCardProps {
  caseObject: Case;
  onClick: (itemId: string, status?: string) => void;
  onDelete: (itemId: string) => void;
  isDeleting: boolean;
}

const CaseCard = ({ caseObject, onClick, onDelete, isDeleting }: CaseCardProps) => {
  const { user } = useUserContext();
  const { hasPermission } = useHasPermission();

  const isOwner = user?.id === caseObject.createdById;

  const isCaseProcessing = caseObject.runs.at(0) && isProcessingStatus(caseObject.runs[0].status as RunStatus);
  const isCaseAwaitingApproval = caseObject.runs.at(0) && caseObject.runs[0].status === RunStatus.AwaitingApproval;
  const canDelete = (hasPermission(Permission.CaseDeleteAny) || isOwner) && !isCaseProcessing;

  // Truncate legal issues for display, show full on hover
  const legalIssues = caseObject.legalIssues;

  const getStatusBadge = () => {
    if (isCaseProcessing) {
      if (isCaseAwaitingApproval) {
        return (
          <span className="flex items-center gap-1.5 rounded-md border border-yellow-200 bg-yellow-50 px-2 py-0.5 text-xs font-normal text-yellow-700 shadow-sm">
            <TriangleAlert className="h-3 w-3" />
            <span>Review Required</span>
          </span>
        );
      }
      return (
        <span className="flex items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-normal text-blue-700 shadow-sm">
          <LoaderCircle className="h-3 w-3 animate-spin" />
          <span>Processing</span>
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-normal text-gray-500">
        <CheckCircle className="h-3 w-3 text-green-400" />
        <span>Processed</span>
      </span>
    );
  };

  return (
    <TooltipProvider>
      <div
        className={cn(
          'group relative h-full w-full rounded-lg border border-gray-200 bg-white shadow-sm transition-all duration-200 hover:bg-gray-50 hover:shadow-md',
          'flex cursor-pointer flex-col',
          isCaseProcessing ? 'border-dashed border-blue-200' : '',
          isCaseAwaitingApproval ? 'border-yellow-300' : '',
        )}
        onClick={() => onClick(caseObject.id, caseObject.runs[0]?.status)}
        tabIndex={0}
        aria-label={`Open case ${caseObject.name}`}
      >
        <div className="flex w-full items-center justify-between gap-4 rounded-t-lg border-b border-dashed border-gray-200 bg-gray-100 px-4 py-2">
          <div className="min-w-0 flex-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="max-w-full truncate font-bold text-gray-800 group-hover:text-blue-600">
                  {caseObject.name}
                </div>
              </TooltipTrigger>
              <TooltipContent className="font-medium">{caseObject.name}</TooltipContent>
            </Tooltip>
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            {canDelete && (
              <Button
                data-testid="delete-btn"
                variant="ghost"
                size="sm"
                className="ml-1 h-8 w-8 text-gray-400 hover:bg-red-100 hover:text-red-600"
                onClick={(e) => {
                  e.stopPropagation();
                  trackCustomEvent(TrackingEvents.DeleteCase, {
                    caseId: caseObject.id,
                  });
                  onDelete(caseObject.id);
                }}
                disabled={isDeleting}
                aria-label="Delete case"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Description / Legal Issues */}
        <div className="p-4">
          <div className="mt-3 flex items-start gap-2">
            <FileText className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
            <div className="line-clamp-1 max-w-full truncate text-sm leading-relaxed text-gray-700">{legalIssues}</div>
          </div>

          <div className="mt-6 flex items-end justify-between">
            {/* Date */}
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <Calendar className="h-3 w-3" />
              <span>{formatDate(caseObject.createdDate)}</span>
            </div>
            <div className="flex-shrink-0">{getStatusBadge()}</div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default CaseCard;
